import { memo } from 'react';
import type { ModelElementId } from '@view-model/domain/key';
import type { ModelCommentContents } from './domain/ModelCommentContents';
import { ModelCommentThreadView } from './components';
import type { Size } from '@view-model/models/common/basic/Size';
import type { SelectedItemSet } from '@view-model/models/common/basic';
import type { ModelCommentThreadId, ModelId, ViewModelId } from 'balus-common';
import type { DragContext } from '@model-framework/ui';

type Props = {
  viewModelId: ViewModelId;
  modelId: ModelId;
  commentContents: ModelCommentContents;
  onUpdateCommentSize(id: ModelCommentThreadId, newSize: Size): void;
  selectedItems: SelectedItemSet<ModelElementId>;
  urlOpenThreadId: ModelCommentThreadId | null;
  readonly: boolean;
  onDragStart(id: ModelCommentThreadId, e: React.MouseEvent<SVGGElement>): void;
  onDrag(context: DragContext): void;
  onDragEnd(context: DragContext): void;
  onClick(id: ModelCommentThreadId, e: React.MouseEvent<SVGGElement>): void;
  onClose(id: ModelCommentThreadId): void;
};

export const ModelCommentList = memo(
  ({
    viewModelId,
    modelId,
    commentContents,
    onUpdateCommentSize,
    selectedItems,
    urlOpenThreadId,
    readonly,
    onDragStart,
    onDrag,
    onDragEnd,
    onClick,
    onClose,
  }: Props) => {
    // SVG は後から描画した要素が前面に表示されるため、
    // 展開中（isSelected）のスレッドを未選択スレッドより後に描画することで前面に固定する
    const threads = commentContents.map(([thread, position]) => ({
      thread,
      position,
      // RTDB の selectedItems に加え、URL パラメータで指定されたスレッドも表示対象とする
      isSelected: selectedItems.isSelected(thread.id) || thread.id === urlOpenThreadId,
    }));

    const renderThread = ({ thread, position, isSelected }: (typeof threads)[number]) => (
      <ModelCommentThreadView
        key={thread.id}
        viewModelId={viewModelId}
        modelId={modelId}
        commentThread={thread}
        position={position}
        readonly={readonly}
        isSelected={isSelected}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        onClick={onClick}
        onSizeChange={onUpdateCommentSize}
        onClose={onClose}
      />
    );

    return (
      <g>
        {threads.filter((t) => !t.isSelected).map(renderThread)}
        {threads.filter((t) => t.isSelected).map(renderThread)}
      </g>
    );
  }
);
ModelCommentList.displayName = 'ModelCommentList';
